import { getClusterColor, getScoreColor } from 'utils/helpers';
import ClusterTooltip from '../ClusterTooltip';
import DefinitionTooltip from '../DefinitionTooltip';
import HealthStatTooltip from '../HealthStatTooltip';
import { LifeCycleBadge } from 'components/LifeCycleBadge';
import OverviewCardWrapper from './OverviewCardWrapper';
import { POPULARITY_SORT_OPTIONS } from 'utils/metrics';
import { ParentSize } from '@visx/responsive';
import StackedAreaSparkline from '../DataVis/StackedAreaSparkline';
import { getSubcategoryLabel } from 'utils/hierarchies';
import { memo } from 'react';
import { theme as themeTokens } from 'utils/theme';
import { useTheme } from 'styled-components';

const ROW_1_KEYS = new Set([
    'popularity_index_avg_12_month',
    'predicted_YoY',
    'YoY',
    'increase_YoY',
    'QoQ',
    'increase_QoQ',
    'MoM',
    'increase_MoM',
]);

const GROWTH_PAIRS = {
    YoY: 'increase_YoY',
    QoQ: 'increase_QoQ',
    MoM: 'increase_MoM',
};

const REVERSE_GROWTH_PAIRS = Object.fromEntries(
    Object.entries(GROWTH_PAIRS).map(([pct, abs]) => [abs, pct]),
);

function getGrowthCell(data, pctKey, absKey, sortBy) {
    const activeKey = sortBy === absKey ? absKey : pctKey;
    const activeOption = POPULARITY_SORT_OPTIONS[activeKey];
    const rawValue = data?.[activeKey];
    return {
        value: activeOption?.format(rawValue),
        label: POPULARITY_SORT_OPTIONS[pctKey]?.label || pctKey,
        rawValue,
        tooltip: activeOption?.tooltip,
        mixpanelContentKey: activeOption?.mixpanelContentKey,
    };
}

const HEALTH_STAT_TO_SORT_KEY = {
    'average monthly popularity': 'popularity_index_avg_12_month',
    'popularity predicted growth': 'predicted_YoY',
    'popularity convergence': 'convergence_prc',
    'popularity share': 'google_share_prc',
};

function getActiveGrowthMetric(sortBy) {
    if (!sortBy) return 'YoY';
    // If sorting by a growth metric, use that pair
    if (GROWTH_PAIRS[sortBy]) return sortBy;
    if (REVERSE_GROWTH_PAIRS[sortBy]) return REVERSE_GROWTH_PAIRS[sortBy];
    // Default to YoY
    return 'YoY';
}

// The health stats row 2 actually renders -- keep in step with getRow2Metrics
// below. A filter on anything else has no cell to show, so it must not grow
// the card.
const ROW_2_HEALTH_STATS = ['popularity share', 'popularity convergence'];

export function hasRow2Filter(filters) {
    const hasHealthStatFilter =
        filters?.healthStats?.length > 0 &&
        filters.healthStats.some((f) =>
            ROW_2_HEALTH_STATS.includes(f.metricLabel),
        );
    const hasClusterFilter = filters?.cluster?.length > 0;
    return hasHealthStatFilter || hasClusterFilter;
}

function getRow2Metrics(data) {
    const shareStat = data?.healthStats?.['popularity share'];
    const shareSource =
        shareStat?.score >= 8
            ? 'Google'
            : shareStat?.score >= 7
              ? 'TikTok'
              : shareStat?.score >= 6
                ? 'Instagram'
                : null;
    const convStat = data?.healthStats?.['popularity convergence'];

    return [
        {
            value: data?.cluster || '—',
            label: 'Cluster',
            textMetric: true,
            hoverColor: getClusterColor(data?.cluster),
            tooltip: { type: 'cluster', cluster: data?.cluster },
        },
        {
            value: shareStat?.metric,
            label: shareStat?.scoreLabel || shareSource || 'Popularity Share',
            labelLines: 1,
            hoverColor: shareStat?.scoreLabel
                ? getScoreColor(shareStat.score, false, 'popularity share')
                : null,
            tooltip: {
                type: 'healthStat',
                metricLabel: 'popularity share',
                scoreLabel: shareStat?.scoreLabel,
            },
        },
        {
            value: convStat?.metric,
            label: convStat?.scoreLabel || 'Convergence',
            labelLines: 1,
            hoverColor: convStat?.scoreLabel
                ? getScoreColor(convStat.score, false, 'popularity convergence')
                : null,
            tooltip: {
                type: 'healthStat',
                metricLabel: 'popularity convergence',
                scoreLabel: convStat?.scoreLabel,
            },
        },
    ];
}

function MetricCellContent({
    value,
    label,
    labelLines = 2,
    isLast,
    textMetric,
    hoverColor,
}) {
    const valueBase = textMetric
        ? 'text-xs font-semibold leading-tight line-clamp-2 capitalize'
        : 'text-sm font-semibold leading-none truncate uppercase';
    const valueClass = hoverColor
        ? `${valueBase} transition-colors group-hover/cell:text-[var(--metric-hover-color)]`
        : valueBase;
    const labelWrap =
        labelLines === 2 ? 'line-clamp-2 text-pretty' : 'truncate';
    const labelBase = `text-[10px] leading-[14px] text-gray-500 capitalize transition-colors ${labelWrap}`;
    const labelClass = hoverColor
        ? `${labelBase} group-hover/cell:text-[var(--metric-hover-color)]`
        : labelBase;
    const cellStyle = hoverColor
        ? { '--metric-hover-color': hoverColor }
        : undefined;
    return (
        <div
            className={`flex flex-col justify-start gap-1 px-3 py-1.5 min-w-0 overflow-hidden group/cell ${!isLast ? 'border-r border-gray-100' : ''}`}
            style={cellStyle}
        >
            <span className={valueClass}>{value ?? '—'}</span>
            <span className={labelClass}>{label}</span>
        </div>
    );
}

function MetricCell({ tooltip, ...props }) {
    const content = <MetricCellContent {...props} />;
    if (!tooltip) return content;

    try {
        if (tooltip.type === 'cluster' && tooltip.cluster) {
            return (
                <ClusterTooltip
                    cluster={tooltip.cluster}
                    dataSource="popularity"
                >
                    <span>{content}</span>
                </ClusterTooltip>
            );
        }
        if (tooltip.type === 'definition' && tooltip.tooltip) {
            return (
                <DefinitionTooltip
                    {...tooltip.tooltip}
                    mixpanelMeta={{ contentKey: tooltip.mixpanelContentKey }}
                >
                    <span>{content}</span>
                </DefinitionTooltip>
            );
        }
        if (
            tooltip.type === 'healthStat' &&
            tooltip.metricLabel &&
            tooltip.scoreLabel
        ) {
            return (
                <HealthStatTooltip
                    metricLabel={tooltip.metricLabel}
                    label={tooltip.scoreLabel}
                >
                    <span>{content}</span>
                </HealthStatTooltip>
            );
        }
    } catch {
        // Fall back if tooltip data is missing
    }
    return content;
}

function OverviewCard({
    paginateItem: item,
    data,
    timeSeriesGoogle,
    timeSeriesTikTok,
    timeSeriesInstagram,
    onCardClick,
    sortOption,
    filters,
    parentCategory,
    as,
}) {
    const theme = useTheme();
    const series = [];
    if (timeSeriesGoogle)
        series.push({
            id: 'google',
            timeSeries: timeSeriesGoogle,
            color: theme.google_blue,
        });
    if (timeSeriesTikTok)
        series.push({
            id: 'tiktok',
            timeSeries: timeSeriesTikTok,
            color: theme.tiktok_pink,
        });
    if (timeSeriesInstagram)
        series.push({
            id: 'instagram',
            timeSeries: timeSeriesInstagram,
            color: theme.instagram_orange,
        });

    const subcategory = getSubcategoryLabel(item?.hierarchies, parentCategory);

    const sortBy = sortOption?.sortBy;
    const activePctKey = getActiveGrowthMetric(sortBy);
    const activeAbsKey = GROWTH_PAIRS[activePctKey];
    const popScoreLabel =
        data?.healthStats?.['average monthly popularity']?.scoreLabel;
    const popScore = data?.healthStats?.['average monthly popularity']?.score;
    const predScoreLabel =
        data?.healthStats?.['popularity predicted growth']?.scoreLabel;
    const predScore = data?.healthStats?.['popularity predicted growth']?.score;
    const growthCell = getGrowthCell(data, activePctKey, activeAbsKey, sortBy);
    const growthValue = growthCell.rawValue;
    const row1 = [
        {
            value: POPULARITY_SORT_OPTIONS.popularity_index_avg_12_month?.format(
                data?.popularity_index_avg_12_month,
            ),
            label: popScoreLabel || 'Popularity',
            hoverColor: popScoreLabel
                ? getScoreColor(popScore, false, 'average monthly popularity')
                : null,
            tooltip: {
                type: 'healthStat',
                metricLabel: 'average monthly popularity',
                scoreLabel: popScoreLabel,
            },
        },
        {
            value: growthCell.value,
            label: growthCell.label,
            hoverColor:
                growthValue > 0
                    ? themeTokens.green_600
                    : growthValue < 0
                      ? themeTokens.red_500
                      : null,
            tooltip: growthCell.tooltip
                ? {
                      type: 'definition',
                      tooltip: growthCell.tooltip,
                      mixpanelContentKey: growthCell.mixpanelContentKey,
                  }
                : undefined,
        },
        {
            value: POPULARITY_SORT_OPTIONS.predicted_YoY?.format(
                data?.predicted_YoY,
            ),
            label: predScoreLabel
                ? `${predScoreLabel} Predicted YoY`
                : 'Predicted YoY',
            hoverColor: predScoreLabel
                ? getScoreColor(predScore, false, 'popularity predicted growth')
                : null,
            tooltip: {
                type: 'healthStat',
                metricLabel: 'popularity predicted growth',
                scoreLabel: predScoreLabel,
            },
        },
    ];

    const showRow2 = hasRow2Filter(filters);
    const row2 = showRow2 ? getRow2Metrics(data) : null;

    return (
        <OverviewCardWrapper
            as={as}
            mixpanelContext="OverviewCard"
            onClick={
                onCardClick
                    ? (e) =>
                          onCardClick({
                              paginateItem: item,
                              data,
                              timeSeriesGoogle,
                              timeSeriesTikTok,
                              timeSeriesInstagram,
                          })
                    : undefined
            }
        >
            <div className="flex items-start justify-between gap-2 px-4 pb-4">
                <div className="flex flex-col gap-0.5 min-w-0">
                    <span className="text-sm font-semibold leading-5 capitalize line-clamp-2">
                        {item?.name}
                    </span>
                    {subcategory && (
                        <span className="text-sm text-gray-500 leading-5 capitalize truncate">
                            {subcategory}
                        </span>
                    )}
                </div>
                <LifeCycleBadge stage={data?.life_cycle} />
            </div>
            {/* The header hugs its name; this soaks up whatever a one-line name
                leaves over, so the chart below it is the same size on every card. */}
            <span className="flex-1 min-h-0" />
            <span className="h-24 shrink-0 flex flex-col pt-3 sparkline-area">
                <span className="w-full h-full">
                    <ParentSize>
                        {({ width, height }) => (
                            <StackedAreaSparkline
                                series={series}
                                width={width}
                                height={height}
                            />
                        )}
                    </ParentSize>
                </span>
            </span>
            <div className="border-t border-gray-200 grid grid-cols-3 bg-gray-50 min-h-14">
                {row1.map((metric, i) => (
                    <MetricCell
                        key={metric.label}
                        {...metric}
                        isLast={i === row1.length - 1}
                    />
                ))}
            </div>
            {row2 && (
                <div className="border-t border-gray-200 grid grid-cols-3 bg-gray-50">
                    {row2.map((metric, i) => (
                        <MetricCell
                            key={metric.label}
                            {...metric}
                            isLast={i === row2.length - 1}
                        />
                    ))}
                </div>
            )}
        </OverviewCardWrapper>
    );
}

export default memo(OverviewCard);
